import { copyDiagnostics } from '../../../utils/callReliability/diagnostics';
import Snackbar from '../../Snackbar/Snackbar';
import React, { useState, useRef } from 'react';
import AboutDialog from '../../AboutDialog/AboutDialog';
import CollaborationViewIcon from '@material-ui/icons/AccountBox';
import DeviceSelectionDialog from '../../DeviceSelectionDialog/DeviceSelectionDialog';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import GridViewIcon from '@material-ui/icons/Apps';
import InfoIconOutlined from '../../../icons/InfoIconOutlined';
import MoreIcon from '@material-ui/icons/MoreVert';

import SettingsIcon from '../../../icons/SettingsIcon';
import {
	Button,
	styled,
	Theme,
	useMediaQuery,
	Menu as MenuContainer,
	MenuItem,
	Typography,
} from '@material-ui/core';

import { useAppState } from '../../../state';
import FlipCameraIcon from '../../../icons/FlipCameraIcon';
import useFlipCameraToggle from '../../../hooks/useFlipCameraToggle/useFlipCameraToggle';

export const IconContainer = styled('div')({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	width: '1.5em',
	marginRight: '0.6em',
	color: '#52606D',
});

export default function Menu(props: { buttonClassName?: string }) {
	const isMobile = useMediaQuery((theme: Theme) =>
		theme.breakpoints.down('sm'),
	);

	const [diagnosticsMessage, setDiagnosticsMessage] = useState('');
	const [aboutOpen, setAboutOpen] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const [settingsOpen, setSettingsOpen] = useState(false);

	const { setIsGalleryViewActive, isGalleryViewActive } = useAppState();

	const anchorRef = useRef<HTMLButtonElement>(null);
	const { flipCameraDisabled, toggleFacingMode, flipCameraSupported } =
		useFlipCameraToggle();

	return (
		<>
			<Button
				onClick={() => setMenuOpen((isOpen) => !isOpen)}
				ref={anchorRef}
				className={props.buttonClassName}
				aria-haspopup="menu"
				aria-expanded={menuOpen}
				data-cy-more-button
			>
				{isMobile ? (
					<>
						<MoreIcon />
						More
					</>
				) : (
					<>
						More
						<ExpandMoreIcon />
					</>
				)}
			</Button>
			<MenuContainer
				open={menuOpen}
				onClose={() => setMenuOpen((isOpen) => !isOpen)}
				anchorEl={anchorRef.current}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'center',
				}}
				transformOrigin={{
					vertical: 'bottom',
					horizontal: 'center',
				}}
			>
				<MenuItem onClick={() => setSettingsOpen(true)}>
					<IconContainer>
						<SettingsIcon />
					</IconContainer>
					<Typography variant="body1">
						Audio and Video Settings
					</Typography>
				</MenuItem>

				{flipCameraSupported && (
					<MenuItem
						disabled={flipCameraDisabled}
						onClick={toggleFacingMode}
					>
						<IconContainer>
							<FlipCameraIcon />
						</IconContainer>
						<Typography variant="body1">Flip Camera</Typography>
					</MenuItem>
				)}

				<MenuItem
					onClick={() => {
						setIsGalleryViewActive((isGallery) => !isGallery);
						setMenuOpen(false);
					}}
				>
					<IconContainer>
						{isGalleryViewActive ? (
							<CollaborationViewIcon fontSize="small" />
						) : (
							<GridViewIcon fontSize="small" />
						)}
					</IconContainer>
					<Typography variant="body1">
						{isGalleryViewActive ? 'Speaker View' : 'Gallery View'}
					</Typography>
				</MenuItem>

				<MenuItem
					onClick={() => {
						void copyDiagnostics()
							.then((message) => {
								setDiagnosticsMessage(message);
								setMenuOpen(false);
							})
							.catch(() =>
								setDiagnosticsMessage(
									'Unable to save diagnostics. Please try again.',
								),
							);
					}}
				>
					<IconContainer>
						<FileCopyOutlinedIcon fontSize="small" />
					</IconContainer>
					<Typography variant="body1">
						Copy call diagnostics
					</Typography>
				</MenuItem>
				<MenuItem onClick={() => setAboutOpen(true)}>
					<IconContainer>
						<InfoIconOutlined />
					</IconContainer>
					<Typography variant="body1">About</Typography>
				</MenuItem>
			</MenuContainer>
			<Snackbar
				open={Boolean(diagnosticsMessage)}
				headline="Call diagnostics"
				message={diagnosticsMessage}
				variant="info"
				handleClose={() => setDiagnosticsMessage('')}
			/>
			<AboutDialog
				open={aboutOpen}
				onClose={() => {
					setAboutOpen(false);
					setMenuOpen(false);
				}}
			/>
			<DeviceSelectionDialog
				open={settingsOpen}
				onClose={() => {
					setSettingsOpen(false);
					setMenuOpen(false);
				}}
			/>
		</>
	);
}
